import { useEffect, useState, useRef } from 'react';
interface CountUpProps {
    end: number;
    duration?: number;
    prefix?: string;
    suffix?: string;
}

const CountUp = ({ end, duration = 2000, prefix = "", suffix = "" }: CountUpProps) => {
    const [count, setCount] = useState(0);
    const countRef = useRef<HTMLSpanElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (countRef.current) {
            observer.observe(countRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        let startTime: number | null = null;
        let animationFrame: number;

        const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * end));

            if (progress < 1) {
                animationFrame = requestAnimationFrame(step);
            }
        };

        animationFrame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(animationFrame);
    }, [isVisible, end, duration]);

    return <span ref={countRef}>{prefix}{count}{suffix}</span>;
};

export const LandingAbout = () => {
    return (
        <section id="about" className="py-32 bg-white dark:bg-pacces-ebony font-sans overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-24">
                    {/* Coluna Esquerda: Conteúdo Técnico */}
                    <div className="space-y-12">
                        <div className="space-y-4">
                            <h2 className="text-5xl md:text-6xl font-serif leading-[0.9] tracking-tighter text-pacces-ebony dark:text-white">
                                O Escritório
                            </h2>
                        </div>

                        <div className="space-y-8 text-lg text-stone-600 dark:text-stone-400 leading-relaxed font-light text-justify">
                            <p>
                                Com uma longa trajetória construída sobre os pilares da ética, comprometimento e trabalho técnico de excelência, o <strong className="font-bold text-pacces-ebony dark:text-white">Pacces Advogados</strong> tem crescimento constante e destaque no mercado jurídico.
                            </p>
                            <p>
                                Sempre pautado no aprimoramento de sua estrutura e tecnologias empregadas, detém as melhores ferramentas para atender às demandas jurídicas atuais.
                            </p>
                            <p>
                                Atualmente concentra sua atuação em produtos bancários, prezando pelos interesses de instituições financeiras, mas, também, detém vasta atuação no setor imobiliário.
                            </p>
                        </div>

                    </div>

                    {/* Coluna Direita: Imagem Institucional */}
                    <div className="relative">
                        <div className="aspect-[4/5] overflow-hidden rounded-sm border border-stone-100 dark:border-white/5 shadow-2xl">
                            <img
                                alt="Estrutura Corporativa - Pacces Advogados"
                                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000 scale-105 hover:scale-100"
                                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80"
                            />
                        </div>
                        {/* Decorative Elements */}
                        <div className="absolute -top-6 -right-6 w-32 h-32 border-r-2 border-t-2 border-pacces-gold/20 -z-10"></div>
                        <div className="absolute -bottom-6 -left-6 w-32 h-32 border-l-2 border-b-2 border-pacces-gold/20 -z-10"></div>
                    </div>
                </div>

                {/* Indicadores / Resultados */}
                <div className="pt-20 border-t border-stone-100 dark:border-white/5">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
                        {/* +de 2000 processos patrocinados */}
                        <div className="space-y-4 group">
                            <div className="text-4xl md:text-5xl font-serif text-pacces-gold tracking-tighter">
                                +<CountUp end={2000} />
                            </div>
                            <div className="text-[12px] uppercase tracking-[0.3em] text-stone-500 font-bold leading-tight group-hover:text-pacces-gold transition-colors duration-300">
                                processos patrocinados
                            </div>
                        </div>

                        {/* + de 350 clientes atendidos */}
                        <div className="space-y-4 group">
                            <div className="text-4xl md:text-5xl font-serif text-pacces-gold tracking-tighter">
                                +<CountUp end={350} />
                            </div>
                            <div className="text-[12px] uppercase tracking-[0.3em] text-stone-500 font-bold leading-tight group-hover:text-pacces-gold transition-colors duration-300">
                                clientes atendidos
                            </div>
                        </div>

                        {/* Atuação a nível nacional */}
                        <div className="space-y-4 group">
                            <div className="text-4xl md:text-5xl font-serif text-pacces-gold tracking-tighter uppercase">
                                <span className="text-2xl md:text-3xl font-vanguard-display tracking-widest pt-2 block">Nacional</span>
                            </div>
                            <div className="text-[12px] uppercase tracking-[0.3em] text-stone-500 font-bold leading-tight group-hover:text-pacces-gold transition-colors duration-300">
                                Atuação a nível nacional
                            </div>
                        </div>

                        {/* +100 imóveis regularizados */}
                        <div className="space-y-4 group">
                            <div className="text-4xl md:text-5xl font-serif text-pacces-gold tracking-tighter">
                                +<CountUp end={100} />
                            </div>
                            <div className="text-[12px] uppercase tracking-[0.3em] text-stone-500 font-bold leading-tight group-hover:text-pacces-gold transition-colors duration-300">
                                imóveis regularizados
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
